'use client';

import { useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

/**
 * VerificationGuard - Enforces email verification
 *
 * Unverified users can ONLY access:
 * - /verify-email (verification page)
 * - /auth/signin (after logout)
 * - /auth/signup (to create account)
 *
 * All other pages redirect to /verify-email
 */
export function VerificationGuard({ children }: { children: React.ReactNode }) {
  const { user, userProfile, loading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    // Wait for auth to load
    if (loading) return;

    // If not logged in, allow auth pages
    if (!user) {
      const authPages = ['/auth/signin', '/auth/signup', '/'];
      if (!authPages.includes(pathname) && !pathname.startsWith('/auth/')) {
        router.push('/auth/signin');
      }
      return;
    }

    // If logged in but email not verified
    if (user && userProfile && !userProfile.emailVerified) {
      // Only allow verification page
      if (pathname !== '/verify-email') {
        router.push('/verify-email');
      }
    }

    // If verified, allow all pages (normal behavior)
  }, [user, userProfile, loading, pathname, router]);

  return <>{children}</>;
}
